import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MessageService } from 'primeng/primeng';
import { MisReportColumn } from '../../models/mis-report-columns';

@Component({
  selector: 'app-mis-column-chooser',
  templateUrl: './mis-column-chooser.component.html',
  styleUrls: ['./mis-column-chooser.component.css']
})
export class MisColumnChooserComponent implements OnInit {
  availableColumns: MisReportColumn[] = [];
  selectedColumns: MisReportColumn[] = [];
  layoutName = '';
  searchText = '';
  // Other saved formats' {misLayoutId, layoutName}, used to block picking a name that
  // collides with one of them (misLayoutId of the format currently being edited, if any,
  // is excluded so renaming a format back to its own current name still works).
  existingLayouts: any[] = [];
  currentLayoutId = 0;
  // Tracked explicitly (rather than as a getter re-derived from selectedColumns on every change
  // detection pass) and synced at every point selectedColumns/searchText can change - avoids
  // relying on mat-checkbox correctly re-reading a computed [checked] binding on its own.
  selectAllChecked = false;

  constructor(public dialogRef: MatDialogRef<MisColumnChooserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any, private message: MessageService) { }

  ngOnInit() {
    this.availableColumns = this.data.availableColumns || [];
    this.selectedColumns = (this.data.selectedColumns || []).map(c => ({ ...c }));
    this.layoutName = this.data.layoutName || '';
    this.existingLayouts = this.data.existingLayouts || [];
    this.currentLayoutId = this.data.currentLayoutId || 0;
    this.syncSelectAllChecked();
  }

  get filteredAvailableColumns(): MisReportColumn[] {
    const text = (this.searchText || '').toLowerCase().trim();
    return this.availableColumns.filter(c => !text || c.header.toLowerCase().includes(text));
  }

  onSearchTextChange() {
    this.syncSelectAllChecked();
  }

  isSelected(col: MisReportColumn): boolean {
    return this.selectedColumns.some(c => c.field === col.field);
  }

  toggleColumn(col: MisReportColumn, checked: boolean) {
    if (checked) {
      if (!this.isSelected(col)) {
        this.selectedColumns = [...this.selectedColumns, { ...col }];
      }
    } else {
      this.selectedColumns = this.selectedColumns.filter(c => c.field !== col.field);
    }
    this.syncSelectAllChecked();
  }

  toggleSelectAll(checked: boolean) {
    if (checked) {
      this.selectAll();
    } else {
      this.deselectAll();
    }
    this.syncSelectAllChecked();
  }

  selectAll() {
    const toAdd = this.filteredAvailableColumns.filter(col => !this.isSelected(col));
    this.selectedColumns = [...this.selectedColumns, ...toAdd.map(col => ({ ...col }))];
    this.syncSelectAllChecked();
  }

  deselectAll() {
    const filteredFields = this.filteredAvailableColumns.map(c => c.field);
    this.selectedColumns = this.selectedColumns.filter(c => !filteredFields.includes(c.field));
    this.syncSelectAllChecked();
  }

  removeColumn(col: MisReportColumn) {
    this.selectedColumns = this.selectedColumns.filter(c => c.field !== col.field);
    this.syncSelectAllChecked();
  }

  private syncSelectAllChecked() {
    const filtered = this.filteredAvailableColumns;
    this.selectAllChecked = filtered.length > 0 && filtered.every(col => this.isSelected(col));
  }

  drop(event: CdkDragDrop<MisReportColumn[]>) {
    moveItemInArray(this.selectedColumns, event.previousIndex, event.currentIndex);
  }

  save() {
    if (!this.selectedColumns.length) {
      this.showTopCenter('warn', 'Failure Message', 'Select at least one column before saving.');
      return;
    }
    if (!this.layoutName || !this.layoutName.trim()) {
      this.showTopCenter('warn', 'Failure Message', 'Please enter report name .');
      return;
    }
    const trimmedName = this.layoutName.trim();
    const duplicate = this.existingLayouts.some(f => f.misLayoutId !== this.currentLayoutId
      && (f.layoutName || '').trim().toLowerCase() === trimmedName.toLowerCase());
    if (duplicate) {
      this.showTopCenter('warn', 'Failure Message',
        `A report format named "${trimmedName}" already exists. Please choose a different name.`);
      return;
    }
    this.dialogRef.close({ layoutName: trimmedName, columns: this.selectedColumns });
  }

  cancel() {
    this.dialogRef.close(null);
  }

  showTopCenter(level: string, info: string, message: string) {
    this.message.add({ severity: level, summary: info, detail: message });
  }
}
